import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { setAuthToken } from "./api";

const BASE = "https://web-production-c0e77.up.railway.app";

/**
 * Kullanıcı oturumu — Supabase magic-link. Token backend'e /api/predict
 * çağrısında Authorization: Bearer olarak gider (analiz artık üyelik ister).
 *
 * Plan (free/pro/max) + kalan hak backend'den (/api/my-plan) çekilir; sayfa
 * açılışında ve her analizden sonra tazelenir.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);     // { plan, count, limit, remaining }
  const [loginOpen, setLoginOpen] = useState(false);
  const [pricingMsg, setPricingMsg] = useState(null);  // null=kapalı, string=mesajla açık
  const tokenRef = useRef(null);

  const fetchPlan = useCallback(async (token) => {
    const t = token || tokenRef.current;
    if (!t) { setPlan(null); return; }
    try {
      const r = await fetch(`${BASE}/api/my-plan`, { headers: { Authorization: `Bearer ${t}` } });
      if (r.ok) setPlan(await r.json());
    } catch { /* sessiz */ }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      tokenRef.current = data.session?.access_token || null;
      setAuthToken(tokenRef.current);
      setLoading(false);
      if (data.session) fetchPlan(data.session.access_token);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      tokenRef.current = s?.access_token || null;
      setAuthToken(tokenRef.current);
      if (s) fetchPlan(s.access_token); else setPlan(null);
    });
    return () => sub.subscription.unsubscribe();
  }, [fetchPlan]);

  // Magic link gönder
  const signIn = useCallback(async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    return error ? { ok: false, error: error.message } : { ok: true };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null); tokenRef.current = null; setAuthToken(null); setPlan(null);
  }, []);

  // Analiz çağrıları taze token için bunu kullanır (ref her zaman güncel).
  const getToken = useCallback(() => tokenRef.current, []);

  // Giriş şart: girişliyse true; değilse giriş modalını açar, false döner.
  const requireLogin = useCallback(() => {
    if (tokenRef.current) return true;
    setLoginOpen(true);
    return false;
  }, []);

  const showPricing = useCallback((msg) => setPricingMsg(msg || ""), []);

  return (
    <AuthContext.Provider value={{
      session, user: session?.user || null, isLoggedIn: !!session,
      loading, plan, signIn, signOut, getToken, refreshPlan: fetchPlan,
      requireLogin, loginOpen, setLoginOpen,
      showPricing, pricingMsg, closePricing: () => setPricingMsg(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth, AuthProvider içinde kullanılmalı");
  return ctx;
}
